// @ts-check

import { CmpApi, CmpApiModel } from '@iabtechlabtcf/cmpapi'
// using the following until the bug is fixed in @iabtcf/stub
// https://raw.githubusercontent.com/InteractiveAdvertisingBureau/iabtcf-es/95c5585d26e751b173e98ce830da2fa2dd5e5368/modules/stub/src/stub.js
// https://github.com/InteractiveAdvertisingBureau/iabtcf-es/pull/192
import cmpstub from './stub'
import { createLog } from '../utils/logger'
import * as TcModelFactory from './TcModelFactory'
import { NonIABModel } from './NonIABModel'
import { fetchGvl } from '../utils/fetch-vendor-lists'
import packageJson from '../../package.json'

const log = createLog()

const { cmp: packageJsonCmp } = packageJson
export class Cmp {
  /**
   * @param {CmpOpts} cmpOpts
   */
  constructor({
    tcModel,
    cvlJson = {},
    nrvJson = {},
    tcString,
    addtlConsent,
    storeTcString,
    persist,
    ui,
    uuid,
    gvlVersion,
    gvlUiVersion,
    repromptVersion,
    nextRepromptVersion,
    nrvString,
    nrvVersion,
    nrvUiVersion,
    cvlVersion,
    cvlUiVersion,
    publisherRestrictions,
    publisherConsents
  }) {
    cmpstub()
    this.tcModel = tcModel
    this.hasUserSelectedTheirPreference = !!tcString
    this.notifyModelUpdate = this.notifyModelUpdate.bind(this)
    this.isReady = this.isReady.bind(this)
    this.openUi = this.openUi.bind(this)
    this.notifyUsersPreferences = this.notifyUsersPreferences.bind(this)
    this.hasUserConsentedToAll = this.hasUserConsentedToAll.bind(this)
    this.getGvl = this.getGvl.bind(this)
    this.consentToAll = this.consentToAll.bind(this)
    this.dissentToAll = this.dissentToAll.bind(this)
    this.hasUserConsentedToMandatory =
      this.hasUserConsentedToMandatory.bind(this)
    this.updateConsent = this.updateConsent.bind(this)
    this.reloadTcModel = this.reloadTcModel.bind(this)
    this.levelChanged = this.levelChanged.bind(this)
    this.runApiMethod = this.runApiMethod.bind(this)
    this.getPurposesByVendorMap = this.getPurposesByVendorMap.bind(this)
    this.getNonRegisteredVendors = this.getNonRegisteredVendors.bind(this)
    this.storeTcString = storeTcString
    this.persist = persist
    this.publisherConsents = publisherConsents
    this.publisherRestrictions = publisherRestrictions
    this.ui = ui
    this.uuid = uuid
    this.lastKnownConsentScreen = undefined
    this.gvlVersion = gvlVersion
    this.gvlUiVersion = gvlUiVersion
    this.acmModel = new NonIABModel({
      vendorList: Object.values(cvlJson),
      consentString: addtlConsent,
      vendorListVersion: cvlVersion
    })
    this.cvlUiVersion = cvlUiVersion
    this.nrvModel = new NonIABModel({
      vendorList: Object.values(nrvJson),
      consentString: nrvString,
      vendorListVersion: nrvVersion
    })
    this.nrvUiVersion = nrvUiVersion
    this.repromptVersion = repromptVersion
    this.nextRepromptVersion = nextRepromptVersion

    this.additionalModel = {
      addtlConsent: '',
      /** @type {string|number|undefined} */
      acmVersion: undefined,
      molGvlVersion: '',
      nrvString: '',
      /** @type {string | number|undefined} */
      nrvVersion: undefined,
      repromptVersion: 1
    }

    const validTCDataP = () =>
      new Promise((resolve) => {
        this.tcModel.gvlIsReady().then(() => {
          let resolved = false
          const done = (tcData) => {
            if (!resolved) {
              resolved = true
              resolve(tcData)
            }
          }
          global.__tcfapi('addEventListener', 2, (tcData) => {
            if (tcData.tcString.length) {
              global.__tcfapi(
                'removeEventListener',
                2,
                () => { },
                tcData.listenerId
              )
              done(tcData)
            }
          })
          global.__tcfapi('getTCData', 2, (tcData) => {
            if (tcData.tcString.length) {
              done(tcData)
            }
          })
        })
      })

    const cmpVersion = Number(this.tcModel.model.cmpVersion)
    this.api = new CmpApi(27, cmpVersion, true, {
      getTCData: (callback, response) => {
        this.runApiMethod(() => this.addAdditionalModelToTCData(response), callback)
      },
      hasUserConsentedToAll: async (callback) => {
        this.runApiMethod(async () => {
          await this.tcModel.gvlIsReady()
          return this.hasUserConsentedToAll()
        }, callback)
      },
      hasUserDissentedToAll: async (callback) => {
        this.runApiMethod(async () => {
          await this.tcModel.gvlIsReady()
          return this.hasUserDissentedToAll()
        }, callback)
      },
      getPurposesByVendorMap: async (callback) => {
        this.runApiMethod(async () => {
          await this.tcModel.gvlIsReady()
          return this.getPurposesByVendorMap()
        }, callback)
      },
      getCustomVendorsConsents: async (callback) => {
        this.runApiMethod(async () => {
          await this.tcModel.gvlIsReady()
          return this.getCustomVendorsConsents()
        }, callback)
      },
      getNonRegisteredVendorsConsents: (callback) => {
        this.runApiMethod(async () => {
          await this.tcModel.gvlIsReady()
          return this.getNonRegisteredVendorsConsents()
        }, callback)
      },
      getConsentDegree: async (callback) => {
        this.runApiMethod(async () => this.getConsentDegree(), callback)
      },
      setUserId: (callback, uuid) => {
        this.runApiMethod(() => {
          this.uuid = uuid
          return null
        }, callback)
      },
      openUi: (callback, { uiLevel, uuid = this.uuid }) => {
        this.runApiMethod(() => {
          this.uuid = uuid
          this.openUi(uiLevel)
        }, callback)
      },
      getValidTCData: async (callback) => {
        this.runApiMethod(async () => validTCDataP(), callback)
      },
      consentedVendors: async (callback) => {
        this.runApiMethod(async () => this.consentedVendors(), callback)
      },
      consentedVendorsDetails: async (callback) => {
        this.runApiMethod(async () => this.consentedVendorsDetails(), callback)
      },
      getStoredRepromptVersion: (callback) => {
        this.runApiMethod(() => this.repromptVersion, callback)
      },
      getNonRegisteredVendors: (callback) => {
        this.runApiMethod(() => this.getNonRegisteredVendors(), callback)
      },
      hasUserConsentedToMandatory: async (callback) => {
        this.runApiMethod(async () => {
          await this.tcModel.gvlIsReady()
          return this.hasUserConsentedToMandatory()
        }, callback)
      }
    })
  }

  /**
   * @param {any} response
   */
  addAdditionalModelToTCData(response) {
    if (typeof response === 'boolean') {
      return response
    }
    response.addtlConsent = CmpApiModel.gdprApplies ? this.additionalModel.addtlConsent : undefined
    response.acmVersion = CmpApiModel.gdprApplies ? this.additionalModel.acmVersion : undefined
    response.molGvlVersion = CmpApiModel.gdprApplies ? this.additionalModel.molGvlVersion : undefined
    response.nrvString = CmpApiModel.gdprApplies ? this.additionalModel.nrvString : undefined
    response.nrvVersion = CmpApiModel.gdprApplies ? this.additionalModel.nrvVersion : undefined
    response.repromptVersion = CmpApiModel.gdprApplies ? this.additionalModel.repromptVersion : undefined
    return response
  }

  async runApiMethod(fn, callback) {
    try {
      const resp = await fn()
      callback(resp, true)
    } catch (e) {
      log(e)
      callback(null, false)
    }
  }

  getGvl() {
    return this.tcModel.gvl
  }

  async notifyModelUpdate() {
    const tcString = this.tcModel.tcString()
    this.hasUserSelectedTheirPreference = true
    const consentDegree = await this.getConsentDegree()
    const addtlConsent = this.acmModel.getConsentString()
    const acmVersion = this.acmModel.getVendorListVersion()
    const nrvString = this.nrvModel.getConsentString()
    const nrvVersion = this.nrvModel.getVendorListVersion()
    const molGvlVersion = this.tcModel.molGvlVersion

    /**
     * @type {import('../persist/Persist').SaveOpts}
     */
    const persistOpts = {
      uuid: this.uuid,
      tcString,
      consentDegree,
      addtlConsent,
      nrvString
    }
    await this.persist.saveTCString(persistOpts)
    this.repromptVersion = this.nextRepromptVersion

    this.additionalModel.addtlConsent = addtlConsent
    this.additionalModel.acmVersion = acmVersion
    this.additionalModel.molGvlVersion = molGvlVersion
    this.additionalModel.nrvString = nrvString
    this.additionalModel.repromptVersion = this.repromptVersion
    this.additionalModel.nrvVersion = nrvVersion

    this.api.update(tcString, false)
  }

  updateConsent(data) {
    log('data', data)

    const formConsentAndLegInt = (pref, keyPrefix) => {
      const consentKey = `${keyPrefix}Consents`
      const legIntKey = `${keyPrefix}LegitimateInterests`
      return Object.entries(pref).reduce(
        (acc, [id, userPreference]) => {
          const { consent, legInterest } = userPreference
          const _id = parseInt(id)
          if (consent) {
            acc[consentKey].push(_id)
          }
          if (legInterest) {
            acc[legIntKey].push(_id)
          }
          return acc
        },
        { [consentKey]: [], [legIntKey]: [] }
      )
    }

    const {
      purposes = {},
      specialFeatures = {},
      vendors = {},
      nonTcfVendors = {},
      nonRegisteredVendors = {}
    } = data
    const { vendorConsents, vendorLegitimateInterests } = formConsentAndLegInt(
      vendors,
      'vendor'
    )
    const { purposeConsents, purposeLegitimateInterests } =
      formConsentAndLegInt(purposes, 'purpose')
    const specialFeatureOptins = Object.entries(specialFeatures).reduce(
      (acc, [specialFeatureId, userPreference]) => {
        const id = parseInt(specialFeatureId)
        if (userPreference.optIn) {
          acc.push(id)
        }
        return acc
      },
      []
    )

    this.acmModel.updateModel(nonTcfVendors)
    this.nrvModel.updateModel(nonRegisteredVendors)
    const options = {
      vendorConsents,
      vendorLegitimateInterests,
      purposeConsents,
      purposeLegitimateInterests,
      specialFeatureOptins,
      consentScreen: this.lastKnownConsentScreen
    }
    log('options:', options)
    this.tcModel.updateUserPreferences(options)
    CmpApiModel.cmpVersion = packageJsonCmp.version
    this.notifyModelUpdate()
    log(
      'model updates',
      vendorConsents,
      vendorLegitimateInterests,
      purposeConsents,
      purposeLegitimateInterests,
      this.acmModel.getConsents(),
      this.nrvModel.getConsents()
    )
  }

  consentToAll() {
    log('user consenting to all')
    this.acmModel.consentToAll()
    this.nrvModel.consentToAll()
    this.tcModel.consentAll({ consentScreen: this.lastKnownConsentScreen })
    CmpApiModel.cmpVersion = packageJsonCmp.version
    this.notifyModelUpdate()
  }

  dissentToAll() {
    log('user dissenting to all')
    this.acmModel.dissentToAll()
    this.nrvModel.dissentToAll()
    this.tcModel.dissentAll({ consentScreen: this.lastKnownConsentScreen })
    CmpApiModel.cmpVersion = packageJsonCmp.version
    this.notifyModelUpdate()
  }

  levelChanged({ currentLevel }) {
    try {
      this.setCurrentLevel(currentLevel)
      log('last known consent screen:', this.lastKnownConsentScreen)
    } catch (e) {
      log(e.message)
    }
  }

  setCurrentLevel(currentLevel) {
    const screen = currentLevel.match(/\d/)
    if (typeof screen[0] === 'undefined') {
      throw new Error('Unknown level: ' + currentLevel)
    }
    this.lastKnownConsentScreen = screen[0]
  }

  isReady() {
    return this.tcModel.gvlIsReady()
  }

  hasUserConsentedToAll() {
    if (!this.hasUserSelectedTheirPreference) return false
    return (
      this.tcModel.hasUserConsentedToAll() &&
      this.acmModel.hasUserConsentedToAll() &&
      this.nrvModel.hasUserConsentedToAll()
    )
  }

  hasUserDissentedToAll() {
    if (!this.hasUserSelectedTheirPreference) return false
    return (
      this.tcModel.hasUserDissentedToAll() &&
      this.acmModel.hasUserDissentedToAll() &&
      this.nrvModel.hasUserDissentedToAll()
    )
  }

  hasUserConsentedToMandatory() {
    if (!this.hasUserSelectedTheirPreference) return false
    return (
      this.tcModel.hasUserConsentedToMandatory() &&
      this.acmModel.hasUserConsentedToAll() &&
      this.nrvModel.hasUserConsentedToAll()
    )
  }

  getCustomVendorsConsents() {
    return this.acmModel.getConsents()
  }

  getNonRegisteredVendorsConsents() {
    return this.nrvModel.getConsents()
  }

  async getConsentDegree() {
    await this.tcModel.gvlIsReady()
    return this.hasUserSelectedTheirPreference
      ? (this.hasUserConsentedToAll() && 'yes') ||
      (this.hasUserDissentedToAll() && 'no') ||
      'partial'
      : undefined
  }

  async consentedVendors() {
    await this.tcModel.gvlIsReady()
    return this.tcModel.consentedVendors()
  }

  async consentedVendorsDetails() {
    await this.tcModel.gvlIsReady()
    return this.tcModel.consentedVendorsDetails()
  }

  getPurposesByVendorMap() {
    const entries = Object.entries(this.tcModel.byPurposeVendorMap())
    const purposesByVendorMap = entries.reduce(
      (acc, [purpose, { consent: vendorIds }]) => {
        vendorIds.forEach((vendorId) =>
          (acc[vendorId] = acc[vendorId] || []).push(purpose)
        )
        return acc
      },
      {}
    )
    return purposesByVendorMap
  }

  getNonRegisteredVendors() {
    return this.nrvModel.getVendors()
  }

  /**
   * @param {UiLevel} uiLevel
   */
  async openUi(uiLevel) {
    if (this.gvlVersion !== this.gvlUiVersion) {
      log(
        `openUi, gvlVersion: ${this.gvlVersion}, gvlUiVersion: ${this.gvlUiVersion}`
      )
      const [, gvl] = /** @type {[string, GVLFetchResponse]} */ (
        await fetchGvl('gvl', this.gvlUiVersion)
      )
      if (typeof gvl === 'string') {
        throw new Error(`unable to download GVL version: ${this.gvlUiVersion}`)
      }
      const tcModel = await TcModelFactory.create({
        type: 'repromptedUser',
        newGvl: gvl,
        existingTcModel: this.tcModel,
        publisherRestrictions: this.publisherRestrictions,
        publisherConsents: this.publisherConsents
      })
      this.tcModel = tcModel
      this.gvlVersion = gvl.version
      this.gvlUiVersion = gvl.version
    }

    if (this.acmModel.getVendorListVersion() !== this.cvlUiVersion) {
      const url = `//cmp.dmgmediaprivacy.co.uk/custom/${this.cvlUiVersion}/vendor-list.json`
      const response = await fetch(url)
      const cvlJson = await response.json()
      this.acmModel = new NonIABModel({
        vendorList: Object.values(cvlJson),
        consentString: this.acmModel.getConsentString(),
        vendorListVersion: this.cvlUiVersion
      })
    }

    if (this.nrvModel.getVendorListVersion().toString() !== this.nrvUiVersion) {
      const url = `//cmp.dmgmediaprivacy.co.uk/nrv/${this.nrvUiVersion}/vendor-list.json`
      const response = await fetch(url)
      const nrvJson = await response.json()
      this.nrvModel = new NonIABModel({
        vendorList: Object.values(nrvJson),
        consentString: this.nrvModel.getConsentString(),
        vendorListVersion: this.nrvUiVersion
      })
    }

    const consentString = this.hasUserSelectedTheirPreference
      ? this.tcModel.tcString()
      : ''
    const cmpModel = {
      tcModel: this.tcModel.model,
      hasUserSelectedTheirPreference: this.hasUserSelectedTheirPreference,
      acmVendors: this.acmModel.getUiModel(),
      nonRegisteredVendors: this.nrvModel.getUiModel()
    }
    const cmpUpdateHandlers = {
      reloadTcModel: this.reloadTcModel,
      updateConsent: this.updateConsent,
      consentToAll: this.consentToAll,
      dissentToAll: this.dissentToAll,
      levelChanged: this.levelChanged
    }

    log('opening UI')
    this.setCurrentLevel(uiLevel)
    this.ui.open(uiLevel, {
      cmpModel,
      cmpUpdateHandlers,
      isNewUser: !this.hasUserSelectedTheirPreference
    })

    this.additionalModel.addtlConsent = this.acmModel.getConsentString()
    this.additionalModel.acmVersion = this.acmModel.getVendorListVersion().toString()
    this.additionalModel.molGvlVersion = this.tcModel.molGvlVersion
    this.additionalModel.nrvString = this.nrvModel.getConsentString()
    this.additionalModel.nrvVersion = this.nrvModel.getVendorListVersion()
    this.additionalModel.repromptVersion = this.repromptVersion
    this.api.update(consentString, true)
  }

  notifyUsersPreferences() {
    const consentString = this.hasUserSelectedTheirPreference
      ? this.tcModel.tcString()
      : ''
    log('notifying users')
    this.additionalModel.addtlConsent = this.acmModel.getConsentString()
    this.additionalModel.acmVersion = this.acmModel.getVendorListVersion().toString()
    this.additionalModel.molGvlVersion = this.tcModel.molGvlVersion
    this.additionalModel.nrvString = this.nrvModel.getConsentString()
    this.additionalModel.nrvVersion = this.nrvModel.getVendorListVersion()
    this.additionalModel.repromptVersion = this.repromptVersion
    this.api.update(consentString, false)
  }

  reloadTcModel(tcString) {
    this.tcModel.reload(tcString)
    this.isReady().then(() => {
      this.hasUserSelectedTheirPreference = true
      this.notifyUsersPreferences()
    })
  }

  setUuid(uuid) {
    this.uuid = uuid
  }
}

/**
 * @param {CreateCmpOpts} opts
 * @returns {Promise<Cmp>}
 */
export async function createCmp({
  gvlRaw,
  cvlJson = {},
  nrvJson = {},
  tcString,
  addtlConsent,
  storeTcString,
  persist,
  ui,
  uuid,
  gvlVersion,
  gvlUiVersion,
  repromptVersion,
  nextRepromptVersion,
  nrvString,
  nrvVersion,
  nrvUiVersion,
  cvlVersion,
  cvlUiVersion,
  migrateUserGvl,
  existingUser,
  newGvlRaw,
  publisherRestrictions,
  publisherConsents
}) {
  const userType = migrateUserGvl
    ? 'exitingUserSimplifiedGvl'
    : existingUser
      ? 'existingUser'
      : 'newUser'
  const tcModel = await TcModelFactory.create({
    type: userType,
    gvl: gvlRaw,
    tcString,
    newGvl: newGvlRaw,
    publisherRestrictions,
    publisherConsents
  })
  return new Cmp({
    tcModel,
    cvlJson,
    nrvJson,
    tcString,
    addtlConsent,
    storeTcString,
    persist,
    ui,
    uuid,
    gvlVersion,
    gvlUiVersion,
    repromptVersion,
    nextRepromptVersion,
    nrvString,
    nrvVersion,
    nrvUiVersion,
    cvlVersion,
    cvlUiVersion,
    publisherRestrictions,
    publisherConsents
  })
}

/**
 * @typedef {Omit<CreateCmpOpts, 'gvlRaw'|'newGvlRaw'|'migrateUserGvl'|'existingUser'> & {tcModel: import('./tcModel').TcModel}} CmpOpts
 */

/**
 * @typedef {Object} CreateCmpOpts
 * @property {GVLRaw} gvlRaw
 * @property {Record<string,*>} cvlJson
 * @property {Record<string,*>} nrvJson
 * @property {string} [tcString]
 * @property {string} [addtlConsent]
 * @property {boolean} storeTcString
 * @property {*} persist
 * @property {*} ui
 * @property {string|undefined} uuid
 * @property {number|string} gvlVersion
 * @property {number|string} gvlUiVersion
 * @property {number} repromptVersion
 * @property {number} nextRepromptVersion
 * @property {string} [nrvString]
 * @property {string} nrvVersion
 * @property {string} nrvUiVersion
 * @property {number|string} cvlVersion
 * @property {number} cvlUiVersion
 * @property {boolean} migrateUserGvl
 * @property {boolean} existingUser
 * @property {GVLRaw|undefined} newGvlRaw
 * @property {Array<number>} publisherRestrictions
 * @property {import('../config/config').PublisherConsents} publisherConsents
 */

/**
 * @typedef {'Level1'|'Level2'} UiLevel
 */

/**
 * @typedef {import('../utils/fetch-vendor-lists').GVLRaw} GVLRaw
 */

/**
 * @typedef {import('../utils/fetch-vendor-lists').GVLFetchResponse} GVLFetchResponse
 */
